import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import {
  Box, Stack, Typography, Button, TextField, InputAdornment,
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, IconButton, Menu, MenuItem, Tooltip, Chip,
  FormControl, InputLabel, Select, Skeleton, alpha, useTheme,
  ListItemIcon, ListItemText, Alert,
} from '@mui/material';
import {
  Add, Search, MoreVert, Edit, DeleteOutline,
  ContentCopy, Sell, LockClock, HowToReg,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feePlanService from '../../services/feePlanService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useDebounce } from '../../hooks/useDebounce';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SearchableSelect from '../../components/common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';
import FeePlanFormDialog from '../../components/fees/FeePlanFormDialog';
import BulkAssignFeesDialog from '../../components/fees/BulkAssignFeesDialog';
import { FeePlanRow, FeePlan } from '../../types/fees';
import { Session, SessionClass } from '../../types/sessions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

/**
 * Fee Plans — the school's blueprints of what a cohort owes per session.
 * List, create, edit, clone (for locked plans), soft-delete.
 *
 * Performance notes
 * -----------------
 * - Filters go straight to the query string; every list request is one call.
 * - Session/class pickers are loaded once and reused.
 * - Table pulls only the summary shape from the server; the full plan (with
 *   items and installments) is fetched only when the admin opens the editor.
 */

const DEFAULT_ROWS_PER_PAGE = 25;

const FeePlansPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const canReadPlans = canRead('fee_plans');
  const canWritePlans = canWrite('fee_plans');

  // Filters (URL-driven)
  const urlSession = searchParams.get('sessionId') ?? '';
  const urlClass = searchParams.get('classId') ?? '';
  const urlSearch = searchParams.get('search') ?? '';
  const urlPage = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);

  const [sessionId, setSessionId] = useState(urlSession);
  const [classId, setClassId] = useState(urlClass);
  const [searchInput, setSearchInput] = useState(urlSearch);
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(urlPage - 1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  // Data
  const [rows, setRows] = useState<FeePlanRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // Skeleton only on the initial mount. Subsequent loads keep old rows visible.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FeePlan | null>(null);
  const [editingLoading, setEditingLoading] = useState(false);

  // Row action menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<FeePlanRow | null>(null);

  // Delete confirm
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeePlanRow | null>(null);

  // Clone confirm
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState<FeePlanRow | null>(null);
  const [cloneName, setCloneName] = useState('');

  // Bulk-assign
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPlanId, setAssignPlanId] = useState<string | null>(null);

  const canWriteFees = canWrite('fees');

  // ─── URL sync ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const next = new URLSearchParams();
    if (sessionId) next.set('sessionId', sessionId);
    if (classId)   next.set('classId', classId);
    if (search)    next.set('search', search);
    if (page > 0)  next.set('page', String(page + 1));
    setSearchParams(next, { replace: true });
  }, [sessionId, classId, search, page, setSearchParams]);

  // ─── Load session + class pickers once ────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ss, cls] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        // Default to active session on first load if not set via URL.
        if (!sessionId) {
          const active = ss.find(s => s.status === 'active') ?? ss[0];
          if (active) setSessionId(active._id);
        }
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load session / class pickers', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load plans ───────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feePlanService.listFeePlans({
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(res.plans);
      setTotal(res.pagination.totalItems);
      setHasLoadedOnce(true);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load fee plans', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, classId, search, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = async (row: FeePlanRow) => {
    setEditingLoading(true);
    try {
      const full = await feePlanService.getFeePlan(row._id);
      setEditing(full);
      setFormOpen(true);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load plan', { variant: 'error' });
    } finally {
      setEditingLoading(false);
      closeMenu();
    }
  };

  const openDelete = (row: FeePlanRow) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
    closeMenu();
  };

  const openClone = (row: FeePlanRow) => {
    setCloneTarget(row);
    setCloneName(`${row.name} (Copy)`);
    setCloneOpen(true);
    closeMenu();
  };

  const openAssign = (row: FeePlanRow) => {
    setAssignPlanId(row._id);
    setAssignOpen(true);
    closeMenu();
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: FeePlanRow) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await feePlanService.deleteFeePlan(deleteTarget._id);
      enqueueSnackbar(`Deleted “${deleteTarget.name}”`, { variant: 'success' });
      setDeleteOpen(false);
      setDeleteTarget(null);
      load();
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to delete plan', { variant: 'error' });
    }
  };

  const confirmClone = async () => {
    if (!cloneTarget) return;
    try {
      await feePlanService.cloneFeePlan(cloneTarget._id, cloneName.trim() || undefined);
      enqueueSnackbar(`Cloned to “${cloneName.trim() || cloneTarget.name + ' (Copy)'}”`, { variant: 'success' });
      setCloneOpen(false);
      setCloneTarget(null);
      load();
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to clone plan', { variant: 'error' });
    }
  };

  const resetFilters = () => {
    setSessionId('');
    setClassId('');
    setSearchInput('');
    setPage(0);
  };

  const currencyFmt = useCurrencyFormat();

  const scopeLabel = (row: FeePlanRow) => {
    const names = (row.classes || []).map(c => c.name).filter(Boolean);
    if (names.length === 0) return 'All classes';
    if (names.length === 1) {
      return row.section?.name ? `${names[0]} — ${row.section.name}` : names[0];
    }
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} + ${names.length - 2} more`;
  };

  const skeletonRows = useMemo(() =>
    Array.from({ length: 6 }).map((_, i) => (
      <TableRow key={i}>
        <TableCell><Skeleton variant="text" width="60%" /></TableCell>
        <TableCell><Skeleton variant="text" width="40%" /></TableCell>
        <TableCell><Skeleton variant="text" width="50%" /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={40} /></TableCell>
        <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
        <TableCell><Skeleton variant="text" width={60} /></TableCell>
        <TableCell align="right"><Skeleton variant="circular" width={24} height={24} /></TableCell>
      </TableRow>
    )),
    []);

  if (!canReadPlans) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">You don't have permission to view fee plans.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>Fee Plans</Typography>
          <Typography variant="body2" color="text.secondary">
            Blueprints of what each cohort owes for a session. Assign a plan to a class to bill every enrolled student.
          </Typography>
        </Box>
        {canWritePlans && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            onClick={openCreate}
            sx={{ borderRadius: 2, alignSelf: 'flex-start' }}
          >
            New plan
          </Button>
        )}
      </Stack>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <SearchableSelect
            label="Session"
            value={sessionId}
            onChange={v => { setSessionId(v); setPage(0); }}
            options={buildSessionOptions(sessions, theme)}
            placeholder="All sessions"
            minWidth={200}
          />
          <SearchableSelect
            label="Class"
            value={classId}
            onChange={v => { setClassId(v); setPage(0); }}
            options={classes.map(c => ({ value: c._id, label: c.name }))}
            placeholder="All classes"
            minWidth={200}
          />
          <TextField
            size="small"
            placeholder="Search by plan name…"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              ),
            }}
          />
          {(sessionId || classId || search) && (
            <Button size="small" onClick={resetFilters}>Reset</Button>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell>Plan</TableCell>
              <TableCell>Session</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Annual total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ pr: 2 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !hasLoadedOnce ? (
              skeletonRows
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Sell sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                    {sessionId || classId || search ? 'No plans match these filters' : 'No fee plans yet'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {canWritePlans
                      ? 'Create your first plan to start billing a cohort.'
                      : 'Ask an admin to create the first plan.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => {
                const locked = Boolean(row.lockedAt);
                return (
                  <TableRow key={row._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                      {row.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {row.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.session?.name ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{scopeLabel(row)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(row.effectiveFrom), 'MMM d, yyyy')}
                        {row.effectiveTo ? ` → ${format(new Date(row.effectiveTo), 'MMM d, yyyy')}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{row.itemCount}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {currencyFmt(row.totalAnnualAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {locked ? (
                        <Tooltip title={`Locked — assignments exist since ${format(new Date(row.lockedAt!), 'MMM d, yyyy')}`}>
                          <Chip
                            size="small"
                            icon={<LockClock sx={{ fontSize: '0.9rem !important' }} />}
                            label="In use"
                            variant="outlined"
                            sx={{
                              height: 22, fontSize: '0.72rem', fontWeight: 500,
                              borderColor: alpha(theme.palette.warning.main, 0.32),
                              color: theme.palette.warning.dark,
                              bgcolor: alpha(theme.palette.warning.main, 0.08),
                              '& .MuiChip-icon': { color: 'inherit', ml: '4px', mr: '-2px' },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          size="small"
                          label="Editable"
                          variant="outlined"
                          sx={{
                            height: 22, fontSize: '0.72rem', fontWeight: 500,
                            borderColor: alpha(theme.palette.success.main, 0.32),
                            color: theme.palette.success.dark,
                            bgcolor: alpha(theme.palette.success.main, 0.08),
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <IconButton
                        size="small"
                        onClick={e => openMenu(e, row)}
                        aria-label="Row actions"
                        disabled={editingLoading}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Row menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} {...elegantMenuWithHintsProps} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {canWriteFees && (
          <MenuItem onClick={() => menuRow && openAssign(menuRow)}>
            <ListItemIcon><HowToReg fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText primary="Assign to cohort" secondary="Bulk-assign this plan to a class" />
          </MenuItem>
        )}
        <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText primary={menuRow?.lockedAt ? 'View' : 'Edit'} />
        </MenuItem>
        {canWritePlans && (
          <MenuItem onClick={() => menuRow && openClone(menuRow)}>
            <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
            <ListItemText primary="Clone" />
          </MenuItem>
        )}
        {canWritePlans && !menuRow?.lockedAt && (
          <MenuItem onClick={() => menuRow && openDelete(menuRow)} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        )}
      </Menu>

      {/* Create / edit dialog */}
      <FeePlanFormDialog
        open={formOpen}
        editing={editing}
        defaultSessionId={sessionId || undefined}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          // A class filter can hide a freshly-created plan (e.g. school-wide,
          // or covers different classes). Reset the class filter and reset to
          // page 1 so the new row is always visible at the top (list is
          // sorted by updatedAt desc).
          if (!editing) {
            setClassId('');
            setSearchInput('');
            setPage(0);
          }
          load();
        }}
      />

      {/* Delete confirm */}
      <ConfirmationDialog
        open={deleteOpen}
        title="Delete fee plan?"
        message={deleteTarget ? `“${deleteTarget.name}” will be archived. Since no assignments exist yet, no student is affected.` : ''}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={confirmDelete}
        onClose={() => setDeleteOpen(false)}
      />

      {/* Clone dialog — reuses ConfirmationDialog with a TextField child */}
      <ConfirmationDialog
        open={cloneOpen}
        title="Clone fee plan"
        message="Creates a new editable copy with the same scope and line items. The original stays intact for any historical assignments."
        confirmText="Clone"
        confirmColor="primary"
        confirmDisabled={!cloneName.trim()}
        onConfirm={confirmClone}
        onClose={() => setCloneOpen(false)}
      >
        <TextField
          fullWidth
          size="small"
          label="Name for the new plan"
          value={cloneName}
          onChange={e => setCloneName(e.target.value)}
          sx={{ mt: 1 }}
          autoFocus
        />
      </ConfirmationDialog>

      {/* Bulk-assign dialog */}
      <BulkAssignFeesDialog
        open={assignOpen}
        initialPlanId={assignPlanId}
        onClose={() => { setAssignOpen(false); setAssignPlanId(null); }}
        onAssigned={() => { load(); }}
      />
    </Box>
  );
};

export default FeePlansPage;
